import type { WorkspaceResponseDto, WorkspaceCanvasResponseDto } from '@/apis/backend/generated/models';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { paths } from 'src/routes/paths';
import { useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/context/organization-context';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import {
  useWorkspacesControllerListWorkspaces,
  useWorkspacesControllerCreateWorkspace,
  useWorkspacesControllerDeleteWorkspace,
  useWorkspacesControllerUpdateWorkspace,
} from '@/apis/backend/generated/workspaces/workspaces';

import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Email';
import ArticleIcon from '@mui/icons-material/Article';
import CampaignIcon from '@mui/icons-material/Campaign';
import VideocamIcon from '@mui/icons-material/Videocam';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import {
  Box,
  Chip,
  Grid,
  Stack,
  Button,
  Dialog,
  Select,
  Tooltip,
  MenuItem,
  Skeleton,
  Snackbar,
  TextField,
  IconButton,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/c/$clientId/workspaces/',
)({
  component: WorkspaceListPage,
});

// ── Helpers ──

function timeAgo(dateStr: string, t: (key: string, defaultVal: string, opts?: Record<string, unknown>) => string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t('workspaces.timeAgo.justNow', 'Just now');
  if (minutes < 60) return t('workspaces.timeAgo.minutesAgo', '{{count}}m ago', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('workspaces.timeAgo.hoursAgo', '{{count}}h ago', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('workspaces.timeAgo.daysAgo', '{{count}}d ago', { count: days });
  if (days < 30) return t('workspaces.timeAgo.weeksAgo', '{{count}}w ago', { count: Math.floor(days / 7) });
  return new Date(dateStr).toLocaleDateString();
}

function getCanvasTypeCounts(canvases: WorkspaceCanvasResponseDto[]) {
  const counts: Record<string, number> = {};
  for (const c of canvases) {
    counts[c.type] = (counts[c.type] || 0) + 1;
  }
  return counts;
}

const TYPE_CONFIG: Record<string, { icon: typeof EmailIcon; label: string; color: string }> = {
  email: { icon: EmailIcon, label: 'Email', color: '#3b82f6' },
  social_ad: { icon: CampaignIcon, label: 'Social', color: '#8b5cf6' },
  video: { icon: VideocamIcon, label: 'Video', color: '#ef4444' },
};

type SortOption = 'updated-desc' | 'updated-asc' | 'created-desc' | 'created-asc' | 'name-asc' | 'name-desc';

function getSortLabels(t: (key: string, defaultVal: string) => string): Record<SortOption, string> {
  return {
    'updated-desc': t('workspaces.sort.updatedDesc', 'Recently updated'),
    'updated-asc': t('workspaces.sort.updatedAsc', 'Least recently updated'),
    'created-desc': t('workspaces.sort.createdDesc', 'Newest first'),
    'created-asc': t('workspaces.sort.createdAsc', 'Oldest first'),
    'name-asc': t('workspaces.sort.nameAsc', 'Name A-Z'),
    'name-desc': t('workspaces.sort.nameDesc', 'Name Z-A'),
  };
}

function sortWorkspaces(workspaces: WorkspaceResponseDto[], sort: SortOption): WorkspaceResponseDto[] {
  return [...workspaces].sort((a, b) => {
    switch (sort) {
      case 'updated-desc': return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      case 'updated-asc': return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      case 'created-desc': return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'created-asc': return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'name-asc': return a.name.localeCompare(b.name);
      case 'name-desc': return b.name.localeCompare(a.name);
      default: return 0;
    }
  });
}

// ── Tag Manager Dialog ──

function TagManagerDialog({
  open,
  onClose,
  workspace,
  orgId,
  clientId,
}: {
  open: boolean;
  onClose: () => void;
  workspace: WorkspaceResponseDto;
  orgId: string;
  clientId: string;
}) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [newTag, setNewTag] = useState('');
  const { mutate: updateWorkspace } = useWorkspacesControllerUpdateWorkspace({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey[0];
            return typeof key === 'string' && key.includes('/workspaces');
          },
        });
      },
    },
  });

  const handleAddTag = () => {
    const tag = newTag.trim().toLowerCase();
    if (!tag || workspace.tags.includes(tag)) return;
    updateWorkspace({
      orgId,
      clientId,
      workspaceId: workspace.id,
      data: { tags: [...workspace.tags, tag] },
    });
    setNewTag('');
  };

  const handleRemoveTag = (tag: string) => {
    updateWorkspace({
      orgId,
      clientId,
      workspaceId: workspace.id,
      data: { tags: workspace.tags.filter((tg) => tg !== tag) },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{t('workspaces.tags.dialogTitle', 'Manage Tags')}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {workspace.name}
        </Typography>

        {workspace.tags.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
            {workspace.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={() => handleRemoveTag(tag)}
                sx={{ borderRadius: '6px' }}
              />
            ))}
          </Stack>
        )}

        <Stack direction="row" spacing={1}>
          <TextField
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddTag(); }}
            placeholder={t('workspaces.tags.addPlaceholder', 'Add tag...')}
            size="small"
            fullWidth
            sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleAddTag}
            disabled={!newTag.trim()}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {t('workspaces.tags.addButton', 'Add')}
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('workspaces.tags.doneButton', 'Done')}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Workspace Card ──

function WorkspaceCard({
  workspace,
  onOpen,
  onDelete,
  onManageTags,
}: {
  workspace: WorkspaceResponseDto;
  onOpen: () => void;
  onDelete: () => void;
  onManageTags: () => void;
}) {
  const { t } = useTranslation('common');
  const typeCounts = getCanvasTypeCounts(workspace.canvases);

  return (
    <Box
      onClick={onOpen}
      sx={{
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transform: 'translateY(-2px)',
        },
        '&:hover .card-actions': { opacity: 1 },
      }}
    >
      {/* Preview area */}
      <Box
        sx={{
          height: 120,
          bgcolor: '#f8f9fb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          position: 'relative',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {workspace.canvases.length === 0 ? (
          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            {t('workspaces.card.emptyWorkspace', 'Empty workspace')}
          </Typography>
        ) : (
          workspace.canvases.slice(0, 4).map((canvas) => {
            const aspect = canvas.width / canvas.height;
            const displayH = 72;
            const displayW = displayH * aspect;
            return (
              <Box
                key={canvas.id}
                sx={{
                  width: Math.min(displayW, 80),
                  height: displayH,
                  bgcolor: 'background.paper',
                  borderRadius: '6px',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {(() => {
                  if (canvas.contentType === 'template') {
                    return <ArticleIcon sx={{ fontSize: 18, color: '#0ea5e9', opacity: 0.5 }} />;
                  }
                  const config = TYPE_CONFIG[canvas.type];
                  if (!config) return null;
                  const Icon = config.icon;
                  return <Icon sx={{ fontSize: 18, color: config.color, opacity: 0.5 }} />;
                })()}
              </Box>
            );
          })
        )}
        {workspace.canvases.length > 4 && (
          <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>
            +{workspace.canvases.length - 4}
          </Typography>
        )}

        {/* Hover actions */}
        <Stack
          className="card-actions"
          direction="row"
          spacing={0.5}
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            opacity: 0,
            transition: 'opacity 0.15s',
          }}
        >
          <Tooltip title={t('workspaces.card.manageTags', 'Manage tags')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onManageTags(); }}
              sx={{ bgcolor: 'background.paper', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', '&:hover': { color: 'primary.main' } }}
            >
              <LocalOfferIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('workspaces.card.deleteWorkspace', 'Delete workspace')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              sx={{ bgcolor: 'background.paper', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', '&:hover': { color: 'error.main' } }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Card body */}
      <Box sx={{ p: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            mb: 0.75,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {workspace.name}
        </Typography>

        {/* Tags */}
        {workspace.tags.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 0.75 }}>
            {workspace.tags.slice(0, 3).map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{ height: 18, fontSize: '9px', borderRadius: '4px' }}
              />
            ))}
            {workspace.tags.length > 3 && (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '9px', lineHeight: '18px' }}>
                +{workspace.tags.length - 3}
              </Typography>
            )}
          </Stack>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {/* Type badges */}
          <Stack direction="row" spacing={0.75}>
            {Object.entries(typeCounts).map(([type, count]) => {
              const config = TYPE_CONFIG[type];
              if (!config) return null;
              const Icon = config.icon;
              return (
                <Stack
                  key={type}
                  direction="row"
                  alignItems="center"
                  spacing={0.25}
                  sx={{
                    bgcolor: `${config.color}10`,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: '4px',
                  }}
                >
                  <Icon sx={{ fontSize: 12, color: config.color }} />
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '10px', fontWeight: 600, color: config.color }}
                  >
                    {count}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>

          {/* Time ago */}
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <AccessTimeIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '10px' }}>
              {timeAgo(workspace.updatedAt, t)}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

// ── Loading Skeletons ──

function LoadingGrid() {
  return (
    <Grid container spacing={2.5}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
          <Box sx={{ borderRadius: '12px', border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Skeleton variant="rectangular" height={120} />
            <Box sx={{ p: 2 }}>
              <Skeleton width="60%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton width="40%" height={16} />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

// ── Page ──

function WorkspaceListPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id || '';
  const clientId = activeClient?.id || '';
  const tp = paths.dashboard.tenant(orgId, clientId);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [sortBy, setSortBy] = useState<SortOption>('updated-desc');
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [tagDialogWorkspace, setTagDialogWorkspace] = useState<WorkspaceResponseDto | null>(null);

  const { data, isLoading } = useWorkspacesControllerListWorkspaces(orgId, clientId, {
    query: { enabled: !!orgId && !!clientId },
  });

  const { mutate: createWorkspace, isPending: isCreating } =
    useWorkspacesControllerCreateWorkspace({
      mutation: {
        onSuccess: (ws) => {
          queryClient.invalidateQueries({
            predicate: (q) => {
              const key = q.queryKey[0];
              return typeof key === 'string' && key.includes('/workspaces');
            },
          });
          navigate({
            to: `${tp.workspaces}/${ws.id}`,
          });
        },
      },
    });

  const { mutate: deleteWorkspace } = useWorkspacesControllerDeleteWorkspace({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey[0];
            return typeof key === 'string' && key.includes('/workspaces');
          },
        });
        setSnackbar({ open: true, message: t('workspaces.snackbar.deleted', 'Workspace deleted') });
      },
    },
  });

  const allWorkspaces = data?.workspaces ?? [];

  // Collect all unique tags across workspaces
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    for (const ws of allWorkspaces) {
      for (const tag of ws.tags) tags.add(tag);
    }
    return [...tags].sort();
  }, [allWorkspaces]);

  // Filter + sort
  const filteredWorkspaces = useMemo(() => {
    let result = allWorkspaces;
    if (filterTag) {
      result = result.filter((ws) => ws.tags.includes(filterTag));
    }
    return sortWorkspaces(result, sortBy);
  }, [allWorkspaces, filterTag, sortBy]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
            {t('workspaces.title', 'Workspaces')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('workspaces.subtitle', 'Design emails and social ads side-by-side')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          disabled={isCreating}
          onClick={() => createWorkspace({ orgId, clientId, data: {} })}
          sx={{
            px: 2.5,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': { boxShadow: '0 2px 8px rgba(25,118,210,0.3)' },
          }}
        >
          {t('workspaces.newWorkspace', 'New Workspace')}
        </Button>
      </Stack>

      {/* Controls: tag filter + sort */}
      {!isLoading && allWorkspaces.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          {/* Tag filter chips */}
          {allTags.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={t('workspaces.filter.all', 'All')}
                size="small"
                variant={filterTag === null ? 'filled' : 'outlined'}
                onClick={() => setFilterTag(null)}
                sx={{ borderRadius: '6px', fontWeight: filterTag === null ? 600 : 400 }}
              />
              {allTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant={filterTag === tag ? 'filled' : 'outlined'}
                  onClick={() => setFilterTag(filterTag === tag ? null : tag)}
                  sx={{ borderRadius: '6px', fontWeight: filterTag === tag ? 600 : 400 }}
                />
              ))}
            </Stack>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Sort */}
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            size="small"
            sx={{
              minWidth: 180,
              '& .MuiSelect-select': { fontSize: '13px', py: 0.75 },
            }}
          >
            {Object.entries(getSortLabels(t)).map(([value, label]) => (
              <MenuItem key={value} value={value} sx={{ fontSize: '13px' }}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      )}

      {/* Content */}
      {isLoading ? (
        <LoadingGrid />
      ) : allWorkspaces.length === 0 ? (
        <Box
          sx={{
            borderRadius: '16px',
            border: '2px dashed',
            borderColor: 'grey.200',
            py: { xs: 6, sm: 10 },
            px: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            bgcolor: '#fafbfc',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '16px',
              bgcolor: 'primary.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2.5,
            }}
          >
            <DashboardCustomizeIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
            {t('workspaces.empty.title', 'Create your first workspace')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 380, mb: 3, lineHeight: 1.6 }}
          >
            {t('workspaces.empty.description', 'Workspaces let you design multiple emails and social ads together. Compare layouts, share assets, and build campaign creatives in one place.')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={isCreating ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            disabled={isCreating}
            onClick={() => createWorkspace({ orgId, clientId, data: {} })}
            sx={{
              px: 4,
              py: 1.25,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              boxShadow: 'none',
              '&:hover': { boxShadow: '0 4px 12px rgba(25,118,210,0.3)' },
            }}
          >
            {t('workspaces.empty.cta', 'Get Started')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filteredWorkspaces.map((ws) => (
            <Grid key={ws.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <WorkspaceCard
                workspace={ws}
                onOpen={() =>
                  navigate({
                    to: `${tp.workspaces}/${ws.id}`,
                  })
                }
                onDelete={() => deleteWorkspace({ orgId, clientId, workspaceId: ws.id })}
                onManageTags={() => setTagDialogWorkspace(ws)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tag manager dialog */}
      {tagDialogWorkspace && (
        <TagManagerDialog
          open
          onClose={() => setTagDialogWorkspace(null)}
          workspace={tagDialogWorkspace}
          orgId={orgId}
          clientId={clientId}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
